import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/ThemeProvider";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import MerchantDashboard from "@/pages/merchant/Dashboard";
import MerchantWallets from "@/pages/merchant/Wallets";
import MerchantInvoices from "@/pages/merchant/Invoices";
import MerchantPayments from "@/pages/merchant/Payments";
import MerchantAPIKeys from "@/pages/merchant/APIKeys";
import MerchantWebhooks from "@/pages/merchant/Webhooks";
import MerchantSettings from "@/pages/merchant/Settings";
import MerchantIntegrations from "@/pages/merchant/Integrations";
import DemoDownloads from "@/pages/merchant/DemoDownloads";
import MerchantBilling from "@/pages/merchant/Billing";
import MerchantSupport from "@/pages/merchant/Support";
import MerchantKnowledgeBase from "@/pages/merchant/KnowledgeBase";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminMerchants from "@/pages/admin/Merchants";
import AdminTransactions from "@/pages/admin/Transactions";
import AdminNetworks from "@/pages/admin/Networks";
import AdminAuditLogs from "@/pages/admin/AuditLogs";
import AdminErrorLogs from "@/pages/admin/ErrorLogs";
import AdminSettings from "@/pages/admin/Settings";
import AdminAlerts from "@/pages/admin/Alerts";
import AdminPlans from "@/pages/admin/Plans";
import AdminBlogs from "@/pages/admin/Blogs";
import AdminPaymentGateway from "@/pages/admin/PaymentGateway";
import AdminPaymentHistory from "@/pages/admin/PaymentHistory";
import AdminBillingTransactions from "@/pages/admin/BillingTransactions";
import AdminPartners from "@/pages/admin/Partners";
import AdminEmailLogs from "@/pages/admin/EmailLogs";
import AdminPartnerWithdrawals from "@/pages/admin/PartnerWithdrawals";
import AdminMailer from "@/pages/admin/Mailer";
import { PartnerProvider } from '@/contexts/PartnerContext';
import PartnerLogin from '@/pages/partner/Login';
import PartnerDashboard from '@/pages/partner/Dashboard';
import PartnerCustomers from '@/pages/partner/Customers';
import PartnerSubPartners from '@/pages/partner/SubPartners'; 
import PartnerCommissions from '@/pages/partner/Commissions';
import PartnerWithdrawals from '@/pages/partner/Withdrawals';
import PartnerSettings from '@/pages/partner/Settings';
import AdminNodeHealth from "@/pages/admin/NodeHealth";
import AdminSupport from "@/pages/admin/Support";
import AdminKnowledgeBase from "@/pages/admin/KnowledgeBase";
import CheckoutPage from "@/pages/CheckoutPage";
import PaymentLinkPage from "@/pages/PaymentLinkPage";
import DocsPage from "@/pages/public/DocsPage";
import DocsNotificationsPage from "@/pages/public/DocsNotificationsPage";
import DocsIntegrationsPage from "@/pages/public/DocsIntegrationsPage";
import BlogsPage from "@/pages/public/BlogsPage";
import BlogPostPage from "@/pages/public/BlogPostPage";
import PrivacyPage from "@/pages/public/PrivacyPage";
import TermsPage from "@/pages/public/TermsPage";
import OnboardingWizard from "@/components/OnboardingWizard";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminMembers from "@/pages/admin/AdminMembers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

// Branding Context
export const BrandingContext = createContext({ brand_name: 'CoinPayUp', logo_data: '', favicon_data: '' });
export const useBranding = () => useContext(BrandingContext);
export const WidgetContext = createContext({ website_widget: { enabled: false, embed_code: '' }, payment_widget: { enabled: false, embed_code: '', price: 0 } });
export const useWidgets = () => useContext(WidgetContext);

const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({ brand_name: 'CoinPayUp', logo_data: '', logo_dark_data: '', logo_light_data: '', favicon_data: '' });

  const applyFavicon = (data) => {
    if (!data) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = data;
  };

  useEffect(() => {
    axios.get(`${API}/admin/settings/branding/public`)
      .then(({ data }) => {
        setBranding(data);
        if (data.favicon_data) applyFavicon(data.favicon_data);
      })
      .catch(() => {});
  }, []);

  return <BrandingContext.Provider value={{ ...branding, refresh: () => axios.get(`${API}/admin/settings/branding/public`).then(({ data }) => { setBranding(data); if (data.favicon_data) applyFavicon(data.favicon_data); }) }}>{children}</BrandingContext.Provider>;
};

const WidgetProvider = ({ children }) => {
  const [widgets, setWidgets] = useState({ website_widget: { enabled: false, embed_code: '' }, payment_widget: { enabled: false, embed_code: '', price: 0 } });

  useEffect(() => {
    axios.get(`${API}/admin/settings/chat-widgets/public`)
      .then(({ data }) => setWidgets(data))
      .catch(() => {});
  }, []);

  return <WidgetContext.Provider value={widgets}>{children}</WidgetContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Check sessionStorage first (impersonated tab), then localStorage (normal session)
    const storedToken = sessionStorage.getItem('token') || localStorage.getItem('token');
    const storedUser  = sessionStorage.getItem('user')  || localStorage.getItem('user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  const updateUser = (updatedFields) => {
    setUser(prev => {
      const updated = { ...prev, ...updatedFields };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  const toggleTestMode = async () => {
    const response = await axios.patch(`${API}/auth/test-mode`);
    updateUser({ test_mode: response.data.test_mode });
    return response.data;
  };

  const login = async (email, password, captcha_token) => {
    const payload = { email, password };
    if (captcha_token) payload.captcha_token = captcha_token;
    const response = await axios.post(`${API}/auth/login`, payload);
    const data = response.data;
    // 2FA required — return indicator to caller without saving token
    if (data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token };
    }
    const { access_token, user: userData } = data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const verifyTOTP = async (temp_token, totp_code) => {
    const response = await axios.post(`${API}/auth/2fa/verify-login`, { temp_token, totp_code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithToken = async (accessToken) => {
    // Backdoor/impersonate login — write ONLY to sessionStorage (tab-specific)
    // This prevents contaminating the admin tab's localStorage session
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const { data: userData } = await axios.get(`${API}/auth/me`);
    sessionStorage.setItem('token', accessToken);
    sessionStorage.setItem('user', JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    // Register now only sends a verification email — does NOT auto-login
    const response = await axios.post(`${API}/auth/register`, data);
    return response.data; // { message, email }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, verifyTOTP, loginWithToken, register, logout, toggleTestMode, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, updateUser } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Show onboarding wizard for new merchants
  if (user.role === 'merchant' && !user.onboarding_completed) {
    return (
      <>
        {children}
        <OnboardingWizard onComplete={() => updateUser({ onboarding_completed: true })} />
      </>
    );
  }

  return children;
};

// Chat widget helpers — work with Tawk.to and other common SDKs
function hideAllChatWidgets() {
  try { window.Tawk_API?.hideWidget?.(); } catch (_) {}
  try { window.$crisp?.push?.(['do', 'chat:hide']); } catch (_) {}
  try { window.Intercom?.('hide'); } catch (_) {}
  try { window.fcWidget?.hide?.(); } catch (_) {}
  try { window.chaport?.api?.setVisibility?.(false); } catch (_) {}
}
function showAllChatWidgets() {
  try { window.Tawk_API?.showWidget?.(); } catch (_) {}
  try { window.$crisp?.push?.(['do', 'chat:show']); } catch (_) {}
  try { window.fcWidget?.show?.(); } catch (_) {}
  try { window.chaport?.api?.setVisibility?.(true); } catch (_) {}
}

function injectEmbedCode(embedCode) {
  const injected = [];
  try {
    const stripped = embedCode.replace(/<script[\s\S]*?<\/script>/gi, '').trim();
    if (stripped) {
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;z-index:99999;bottom:0;right:0;pointer-events:none;';
      container.innerHTML = stripped;
      document.body.appendChild(container);
      injected.push(container);
    }
    const scriptRegex = /<script([\s\S]*?)>([\s\S]*?)<\/script>/gi;
    let match;
    while ((match = scriptRegex.exec(embedCode)) !== null) {
      try {
        const attrs = match[1];
        const content = match[2];
        const el = document.createElement('script');
        const srcMatch = attrs.match(/src=["']([^"']+)["']/i);
        if (srcMatch) el.src = srcMatch[1];
        else el.textContent = content;
        el.async = true;
        document.body.appendChild(el);
        injected.push(el);
      } catch(e) {
        console.warn('[CoinPayUp] Chat widget script injection skipped:', e.message);
      }
    }
  } catch(e) {
    console.warn('[CoinPayUp] Chat widget injection error:', e.message);
  }
  return injected;
}

const WebsiteWidgetInjector = () => {
  const { website_widget, payment_widget } = useWidgets();
  const location = useLocation();

  // STRICTLY landing page only — every other route hides the widget
  const isLandingPage = location.pathname === '/';
  const isPaymentPage = location.pathname.startsWith('/p/');

  // Website widget: show ONLY on the landing page (/)
  useEffect(() => {
    if (!website_widget?.enabled || !website_widget?.embed_code) return;

    if (!isLandingPage) {
      // Actively hide via SDK API — this covers persistent SDKs like Tawk.to
      // that create their own iframes which survive script-tag removal
      hideAllChatWidgets();
      return;
    }

    // Landing page — show if SDK already loaded (user navigated back to /)
    const alreadyLoaded = !!(window.Tawk_API?.hideWidget || window.$crisp || window.fcWidget?.show);
    if (alreadyLoaded) {
      showAllChatWidgets();
      return;
    }

    // First load on landing page — inject the embed code
    const injected = injectEmbedCode(website_widget.embed_code);

    // Safety net: if user navigates away before SDK finishes loading, hide on load
    const scheduleHideOnLoad = () => {
      if (!window.Tawk_API) return;
      const prev = window.Tawk_API.onLoad;
      window.Tawk_API.onLoad = function () {
        if (window.location.pathname !== '/') hideAllChatWidgets();
        if (typeof prev === 'function') prev();
      };
    };
    scheduleHideOnLoad();

    return () => {
      injected.forEach(el => el.remove?.());
      hideAllChatWidgets();
    };
  }, [isLandingPage, website_widget?.enabled, website_widget?.embed_code]);

  // Payment widget: show only on /p/:slug pages
  useEffect(() => {
    if (!isPaymentPage || !payment_widget?.enabled || !payment_widget?.embed_code) return;
    const injected = injectEmbedCode(payment_widget.embed_code);
    return () => injected.forEach(el => el.remove?.());
  }, [isPaymentPage, payment_widget?.enabled, payment_widget?.embed_code]);

  return null;
};

// ── PostHog: SPA page view tracker + user identification ──────────────────
const PostHogPageView = () => {
  const location = useLocation();
  const posthog = usePostHog();
  const { user } = useAuth();

  // Track page view on every route change
  useEffect(() => {
    if (posthog) {
      posthog.capture('$pageview', { $current_url: window.location.href });
    }
  }, [location.pathname, posthog]);

  // Identify user when logged in
  useEffect(() => {
    if (posthog && user?.id) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.full_name,
        role: user.role,
        plan: user.plan,
        business_name: user.business_name,
      });
    } else if (posthog && !user) {
      posthog.reset(); // Reset on logout
    }
  }, [user, posthog]);

  return null;
};

function App() {
  return (
  <ThemeProvider>
    <BrandingProvider>
    <WidgetProvider>
    <AuthProvider>
    <PartnerProvider>
      <BrowserRouter>
        <PostHogPageView />        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/guru" element={<AdminLoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/checkout/:invoiceId" element={<CheckoutPage />} />
          <Route path="/p/:slug" element={<PaymentLinkPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/notifications" element={<DocsNotificationsPage />} />
          <Route path="/docs/integrations" element={<DocsIntegrationsPage />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/blogs/:slug" element={<BlogPostPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          
          {/* Merchant Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute><MerchantDashboard /></ProtectedRoute>
          } />
          <Route path="/wallets" element={
            <ProtectedRoute><MerchantWallets /></ProtectedRoute>
          } />
          <Route path="/invoices" element={
            <ProtectedRoute><MerchantInvoices /></ProtectedRoute>
          } />
          <Route path="/payments" element={
            <ProtectedRoute><MerchantPayments /></ProtectedRoute>
          } />
          <Route path="/api-keys" element={
            <ProtectedRoute><MerchantAPIKeys /></ProtectedRoute>
          } />
          <Route path="/webhooks" element={
            <ProtectedRoute><MerchantWebhooks /></ProtectedRoute>
          } />
          <Route path="/integrations" element={
            <ProtectedRoute><MerchantIntegrations /></ProtectedRoute>
          } />
          <Route path="/demo-downloads" element={
            <ProtectedRoute><DemoDownloads /></ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute><MerchantSettings /></ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute><MerchantBilling /></ProtectedRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute><MerchantSupport /></ProtectedRoute>
          } />
          <Route path="/knowledge-base" element={
            <ProtectedRoute><MerchantKnowledgeBase /></ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>
          } />
          <Route path="/admin/merchants" element={
            <ProtectedRoute adminOnly><AdminMerchants /></ProtectedRoute>
          } />
          <Route path="/admin/transactions" element={
            <ProtectedRoute adminOnly><AdminTransactions /></ProtectedRoute>
          } />
          <Route path="/admin/networks" element={
            <ProtectedRoute adminOnly><AdminNetworks /></ProtectedRoute>
          } />
          <Route path="/admin/audit-logs" element={
            <ProtectedRoute adminOnly><AdminAuditLogs /></ProtectedRoute>
          } />
          <Route path="/admin/error-logs" element={
            <ProtectedRoute adminOnly><AdminErrorLogs /></ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>
          } />
          <Route path="/admin/alerts" element={
            <ProtectedRoute adminOnly><AdminAlerts /></ProtectedRoute>
          } />
          <Route path="/admin/plans" element={
            <ProtectedRoute adminOnly><AdminPlans /></ProtectedRoute>
          } />
          <Route path="/admin/blogs" element={
            <ProtectedRoute adminOnly><AdminBlogs /></ProtectedRoute>
          } />
          <Route path="/admin/payment-gateway" element={
            <ProtectedRoute adminOnly><AdminPaymentGateway /></ProtectedRoute>
          } />
          <Route path="/admin/payment-history" element={
            <ProtectedRoute adminOnly><AdminPaymentHistory /></ProtectedRoute>
          } />
          <Route path="/admin/billing-transactions" element={
            <ProtectedRoute adminOnly><AdminBillingTransactions /></ProtectedRoute>
          } />
          <Route path="/admin/partners" element={
            <ProtectedRoute adminOnly><AdminPartners /></ProtectedRoute>
          } />
          <Route path="/admin/email-logs" element={
            <ProtectedRoute adminOnly><AdminEmailLogs /></ProtectedRoute>
          } />
          <Route path="/admin/partner-withdrawals" element={
            <ProtectedRoute adminOnly><AdminPartnerWithdrawals /></ProtectedRoute>
          } />
          <Route path="/admin/mailer" element={
            <ProtectedRoute adminOnly><AdminMailer /></ProtectedRoute>
          } />
          <Route path="/admin/node-health" element={
            <ProtectedRoute adminOnly><AdminNodeHealth /></ProtectedRoute>
          } />
          <Route path="/admin/support" element={
            <ProtectedRoute adminOnly><AdminSupport /></ProtectedRoute>
          } />
          <Route path="/admin/members" element={
            <ProtectedRoute adminOnly><AdminMembers /></ProtectedRoute>
          } />
          <Route path="/admin/knowledge-base" element={
            <ProtectedRoute adminOnly><AdminKnowledgeBase /></ProtectedRoute>
          } />

          {/* Partner Portal Routes */}
          <Route path="/partners" element={<PartnerLogin />} />
          <Route path="/partners/dashboard" element={<PartnerDashboard />} />
          <Route path="/partners/customers" element={<PartnerCustomers />} />
          <Route path="/partners/sub-partners" element={<Navigate to="/partners/dashboard" replace />} />
          <Route path="/partners/commissions" element={<PartnerCommissions />} />
          <Route path="/partners/withdrawals" element={<PartnerWithdrawals />} />
          <Route path="/partners/settings" element={<PartnerSettings />} />
        </Routes>
        <WebsiteWidgetInjector />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </PartnerProvider>
    </AuthProvider>
    </WidgetProvider>
    </BrandingProvider>
  </ThemeProvider>
  );
}

export default App;
